import { useCallback, useMemo, useState } from 'react';
import CustomTable from '../../../../shared/components/CustomTable';
import { useSubscriptionTypes } from '../../../../hooks/useSubscriptionTypes';
import ClientAddOnsPanel from './ClientAddOnsPanel';
import { buildColumns } from './columns';
import { ClientAction, ClientFormStep, EMPTY_COPY, TABLE_BODY_HEIGHT_PX, TABLE_MIN_WIDTH_PX } from '../../constants';
import type { ClientRecord } from '../../../../types/ClientManagementTypes';
import styles from './ClientTables.module.scss';

interface ClientsTableProps {
  clients: ClientRecord[];
  isFiltered: boolean;
  onAction: (action: ClientAction, client: ClientRecord, anchor: HTMLElement | null) => void;
  onOpenClient: (client: ClientRecord, step: ClientFormStep) => void;
}

const ClientsTable = ({ clients, isFiltered, onAction, onOpenClient }: ClientsTableProps) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);
  const { subscriptionTypesById } = useSubscriptionTypes();

  const handleToggleExpand = useCallback((clientId: number) => {
    setExpandedRowKeys((keys) => (keys.includes(clientId) ? keys.filter((key) => key !== clientId) : [...keys, clientId]));
  }, []);

  // Only rows flagged hasAddons expand; the column hides their chevron otherwise.
  const renderAddOns = useCallback(
    (record: ClientRecord, _index: number, _indent: number, expanded: boolean) =>
      // Null when collapsed: rc-table only hides opened rows, leaving the fetch running.
      expanded ? (
        <div className={styles.addOnsWrap}>
          <ClientAddOnsPanel client={record} onOpenSubscription={() => onOpenClient(record, ClientFormStep.SUBSCRIPTION)} />
        </div>
      ) : null,
    [onOpenClient],
  );

  const columns = useMemo(
    () => buildColumns({ expandedRowKeys, onToggleExpand: handleToggleExpand, onAction, onOpenClient, subscriptionTypesById }),
    [expandedRowKeys, handleToggleExpand, onAction, onOpenClient, subscriptionTypesById],
  );

  const handleRow = useCallback(
    (record: ClientRecord) => ({ onClick: () => onOpenClient(record, ClientFormStep.SUMMARY) }),
    [onOpenClient],
  );

  return (
    <CustomTable<ClientRecord>
      className={styles.clientsTable}
      rowKey="id"
      dataSource={clients}
      columns={columns}
      pagination={false}
      tableLayout="fixed"
      onRow={handleRow}
      // Not `virtual`: it gives an expanded row no width, so the add-on table's
      // fixed columns have nothing to align to.
      scroll={{ x: TABLE_MIN_WIDTH_PX, y: TABLE_BODY_HEIGHT_PX }}
      expandable={{
        expandedRowKeys,
        showExpandColumn: false,
        rowExpandable: (record) => record.hasAddons,
        expandedRowRender: renderAddOns,
      }}
      locale={{
        emptyText: <p className={styles.emptyState}>{isFiltered ? EMPTY_COPY.NO_SEARCH_MATCHES : EMPTY_COPY.NO_CLIENTS_IN_GROUP}</p>,
      }}
    />
  );
};

export default ClientsTable;
